import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  StickyNote, 
  Save, 
  HelpCircle, 
  CheckSquare, 
  Plus,
  Trash2,
  MessageSquare,
  Check,
  ArrowRight,
  RefreshCw,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";

const SessionTrackerPage = () => {
  const { token, user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("notes");
  
  // Notes state
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Questions and Actions
  const [questions, setQuestions] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  
  // Form states
  const [newQuestion, setNewQuestion] = useState("");
  const [newQuestionPriority, setNewQuestionPriority] = useState("medium");
  const [newAction, setNewAction] = useState("");
  const [newActionAssignee, setNewActionAssignee] = useState("");

  // Get meeting ID from URL params or localStorage
  const getMeetingId = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('meetingId') || localStorage.getItem('currentMeetingId');
  };

  const fetchMeeting = async () => {
    const meetingId = getMeetingId();
    if (!meetingId || !token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeeting(response.data);
      setNotes(response.data.notes || "");
      setQuestions(response.data.questions || []);
      setActionItems(response.data.action_items || []);
    } catch (error) {
      console.error("Failed to fetch meeting:", error);
      toast.error("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeeting();
    // Poll for updates every 5 seconds to stay in sync
    const interval = setInterval(fetchMeeting, 5000);
    return () => clearInterval(interval);
  }, [token]);

  // Update meeting helper
  const updateMeeting = async (updates) => {
    if (!meeting) return;
    try {
      const response = await axios.patch(
        `${API}/meetings/${meeting.id}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMeeting(response.data);
      return response.data;
    } catch (error) {
      toast.error("Failed to save changes");
      throw error;
    }
  };

  // Auto-save notes
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (notesText) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          try {
            setIsSaving(true);
            await updateMeeting({ notes: notesText });
            setHasChanges(false);
            toast.success("Notes saved", { duration: 1500 });
          } catch (error) {
            // Error handled in updateMeeting
          } finally {
            setIsSaving(false);
          }
        }, 1500);
      };
    })(),
    [meeting, token]
  );

  const handleNotesChange = (e) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    setHasChanges(true);
    debouncedSave(newNotes);
  };

  const handleManualSave = async () => {
    try {
      setIsSaving(true);
      await updateMeeting({ notes });
      setHasChanges(false);
      toast.success("Notes saved");
    } catch (error) {
      // Error handled
    } finally {
      setIsSaving(false);
    }
  };

  // Questions management
  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) return;
    const newQ = {
      id: `q-${Date.now()}`,
      question: newQuestion,
      answer: "",
      status: "open",
      priority: newQuestionPriority,
      created_at: new Date().toISOString(),
      answered_at: null
    };
    const updatedQuestions = [...questions, newQ];
    setQuestions(updatedQuestions);
    setNewQuestion("");
    setNewQuestionPriority("medium");
    
    try {
      await updateMeeting({ questions: updatedQuestions });
      toast.success("Question added");
    } catch (error) {
      setQuestions(questions); // Revert
    }
  };

  const updateQuestion = async (questionId, updates) => {
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const updated = { ...q, ...updates };
        if (updates.status === "answered" && !q.answered_at) {
          updated.answered_at = new Date().toISOString();
        }
        return updated;
      }
      return q;
    });
    setQuestions(updatedQuestions);
    await updateMeeting({ questions: updatedQuestions });
  };

  const deleteQuestion = async (questionId) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(updatedQuestions);
    await updateMeeting({ questions: updatedQuestions });
    toast.success("Question removed");
  };

  // Action items management
  const handleAddAction = async () => {
    if (!newAction.trim()) return;
    const newA = {
      id: `a-${Date.now()}`,
      description: newAction,
      assigned_to: newActionAssignee,
      due_date: null,
      status: "pending",
      created_at: new Date().toISOString(),
      completed_at: null
    };
    const updatedActions = [...actionItems, newA];
    setActionItems(updatedActions);
    setNewAction("");
    setNewActionAssignee("");
    
    try {
      await updateMeeting({ action_items: updatedActions });
      toast.success("Action item added");
    } catch (error) {
      setActionItems(actionItems); // Revert
    }
  };

  const updateActionItem = async (actionId, updates) => {
    const updatedActions = actionItems.map(a => {
      if (a.id === actionId) {
        const updated = { ...a, ...updates };
        if (updates.status === "completed" && !a.completed_at) {
          updated.completed_at = new Date().toISOString();
        }
        return updated;
      }
      return a;
    });
    setActionItems(updatedActions);
    await updateMeeting({ action_items: updatedActions });
  };

  const deleteActionItem = async (actionId) => {
    const updatedActions = actionItems.filter(a => a.id !== actionId);
    setActionItems(updatedActions);
    await updateMeeting({ action_items: updatedActions });
    toast.success("Action item removed");
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: "bg-red-100 text-red-700 border-red-200",
      medium: "bg-amber-100 text-amber-700 border-amber-200",
      low: "bg-slate-100 text-slate-600 border-slate-200"
    };
    return <Badge className={`text-xs ${styles[priority]}`}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: "bg-blue-100 text-blue-700 border-blue-200",
      answered: "bg-green-100 text-green-700 border-green-200",
      follow_up: "bg-amber-100 text-amber-700 border-amber-200",
      pending: "bg-slate-100 text-slate-600 border-slate-200",
      in_progress: "bg-blue-100 text-blue-700 border-blue-200",
      completed: "bg-green-100 text-green-700 border-green-200"
    };
    return <Badge className={`text-xs ${styles[status]}`}>{status.replace("_", " ")}</Badge>;
  };

  const openQuestionsCount = questions.filter(q => q.status === "open").length;
  const pendingActionsCount = actionItems.filter(a => a.status !== "completed").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-sm">
          <StickyNote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700 mb-2">No Active Session</h2>
          <p className="text-slate-500">Start a session in the main window to use the tracker.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <StickyNote className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-900">Session Tracker</h1>
              <p className="text-xs text-slate-500">
                {meeting.customer_name} • {meeting.company}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-700">
              <RefreshCw className="w-3 h-3 mr-1" />
              Live Sync
            </Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-4">
        <div className="flex">
          <button
            onClick={() => setActiveTab("notes")}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === "notes" 
                ? "text-blue-600 border-blue-600" 
                : "text-slate-500 hover:text-slate-700 border-transparent"
            }`}
          >
            <MessageSquare className="w-4 h-4 inline mr-2" />
            Notes
          </button>
          <button
            onClick={() => setActiveTab("questions")}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 relative ${
              activeTab === "questions" 
                ? "text-blue-600 border-blue-600" 
                : "text-slate-500 hover:text-slate-700 border-transparent"
            }`}
          >
            <HelpCircle className="w-4 h-4 inline mr-2" />
            Questions
            {openQuestionsCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                {openQuestionsCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("actions")}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === "actions" 
                ? "text-blue-600 border-blue-600" 
                : "text-slate-500 hover:text-slate-700 border-transparent"
            }`}
          >
            <CheckSquare className="w-4 h-4 inline mr-2" />
            Actions
            {pendingActionsCount > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                {pendingActionsCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-w-2xl mx-auto">
        {/* Notes Tab */}
        {activeTab === "notes" && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <Textarea
              value={notes}
              onChange={handleNotesChange}
              placeholder="Type customer observations, comments, or general notes here..."
              className="min-h-[300px] resize-none text-sm border-slate-200"
            />
            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-slate-400">
                {isSaving ? "Saving..." : hasChanges ? "Unsaved changes" : "All changes saved"}
              </p>
              <Button
                onClick={handleManualSave}
                disabled={isSaving || !hasChanges}
                size="sm"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Questions Tab */}
        {activeTab === "questions" && (
          <div className="space-y-4">
            {/* Add Question Form */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <Input
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Enter customer question..."
                className="mb-3"
                onKeyDown={(e) => e.key === "Enter" && handleAddQuestion()}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Priority:</span>
                  {["high", "medium", "low"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setNewQuestionPriority(p)}
                      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                        newQuestionPriority === p 
                          ? p === "high" ? "bg-red-100 text-red-700 ring-1 ring-red-300" 
                            : p === "medium" ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300" 
                            : "bg-slate-200 text-slate-700 ring-1 ring-slate-300"
                          : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <Button onClick={handleAddQuestion} disabled={!newQuestion.trim()}>
                  <Plus className="w-4 h-4 mr-1" /> Add Question
                </Button>
              </div>
            </div>

            {/* Questions List */}
            {questions.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <HelpCircle className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No questions recorded yet</p>
              </div>
            ) : (
              questions.map((q) => (
                <div key={q.id} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <p className="font-medium text-slate-800">{q.question}</p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {getPriorityBadge(q.priority)}
                      {getStatusBadge(q.status)}
                    </div>
                  </div>
                  
                  <Textarea
                    value={q.answer || ""}
                    onChange={(e) => updateQuestion(q.id, { answer: e.target.value })}
                    placeholder="Enter answer..."
                    className="text-sm min-h-[80px] mb-3"
                  />
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {["open", "answered", "follow_up"].map((status) => (
                        <button
                          key={status}
                          onClick={() => updateQuestion(q.id, { status })}
                          className={`text-xs px-3 py-1.5 rounded transition-colors ${
                            q.status === status 
                              ? status === "open" ? "bg-blue-100 text-blue-700" 
                                : status === "answered" ? "bg-green-100 text-green-700" 
                                : "bg-amber-100 text-amber-700"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {status === "follow_up" ? "Follow-up" : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => deleteQuestion(q.id)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Actions Tab */}
        {activeTab === "actions" && (
          <div className="space-y-4">
            {/* Add Action Form */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <Input
                value={newAction}
                onChange={(e) => setNewAction(e.target.value)}
                placeholder="Enter action item..."
                className="mb-3"
              />
              <div className="flex items-center gap-3">
                <Input
                  value={newActionAssignee}
                  onChange={(e) => setNewActionAssignee(e.target.value)}
                  placeholder="Assigned to (optional)"
                  className="flex-1"
                />
                <Button onClick={handleAddAction} disabled={!newAction.trim()}>
                  <Plus className="w-4 h-4 mr-1" /> Add Action
                </Button>
              </div>
            </div>

            {/* Actions List */}
            {actionItems.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <CheckSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500">No action items yet</p>
              </div>
            ) : (
              actionItems.map((a) => (
                <div 
                  key={a.id} 
                  className={`bg-white rounded-lg shadow-sm p-4 ${
                    a.status === "completed" ? "bg-green-50" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <p className={`font-medium ${a.status === "completed" ? "text-slate-500 line-through" : "text-slate-800"}`}>
                        {a.description}
                      </p>
                      {a.assigned_to && (
                        <p className="text-sm text-slate-500 mt-1">
                          <ArrowRight className="w-3 h-3 inline mr-1" />
                          {a.assigned_to}
                        </p>
                      )}
                    </div>
                    {getStatusBadge(a.status)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {["pending", "in_progress", "completed"].map((status) => (
                        <button
                          key={status}
                          onClick={() => updateActionItem(a.id, { status })}
                          className={`text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1 ${
                            a.status === status 
                              ? status === "pending" ? "bg-slate-200 text-slate-700" 
                                : status === "in_progress" ? "bg-blue-100 text-blue-700" 
                                : "bg-green-100 text-green-700"
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {status === "completed" && <Check className="w-3 h-3" />}
                          {status === "in_progress" ? "In Progress" : status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => deleteActionItem(a.id)}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionTrackerPage;
